import { Country } from '@afterpaytouch/portal-api/types'
import { BiometricsCheckTrigger, CheckoutError, CrossRegionRedirection, InitialData, OtpLoginRouteAndTriggerResult, ConvergenceRollout } from '../../model'
import { AsyncAction, setIntegrationContext } from '../../state'
import { PublicResponse } from '@afterpay/verify-service-api'
import { Route } from '../../config/router'
import { InitialiseNextRoute } from './routing'
import {
  performInitialSetup,
  fetchAndValidateCheckout,
  handlePersistentLogin,
  handleConsolidatedConsumerLookup,
  prepareLookupData,
  performSequentialLookup,
  handleInitialiseError,
  SetupRequest,
  CheckoutFetchRequest,
  ConsumerLookupRequest,
  LookupDataPreparationRequest,
} from './orchestrators'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { isAppleRetailOrder } from '../../state/checkout/helper'
import { getValidUserEmailAndMobile } from '../login-identifier'
import { validateEmailFormat } from './validation'
import { isUndefinedOrEmpty } from '@afterpay/utils'

type InitialiseResult = InitialiseNextRoute | CheckoutError | CrossRegionRedirection | PublicResponse | undefined

export interface InitialiseRequest {
  initialData: InitialData | null
  country: Country
  otpLoginRouteAndTrigger: OtpLoginRouteAndTriggerResult
  biometricsEligibleAndRegisteredCheckTrigger: BiometricsCheckTrigger
  referrer: string | null // = null,
  usernameFromCookie: string | null // = null,
  skipPrefill: boolean // = false
  rollout: ConvergenceRollout
  logEvent: (event: string, data?: Record<string, unknown>) => void
  cashAuthRoute: Route
}

export const initialise =
  (request: InitialiseRequest): AsyncAction<InitialiseResult> =>
  async (dispatch) => {
    try {
      // Step 1: Initial Setup
      const setupRequest: SetupRequest = {
        initialData: request.initialData,
        country: request.country,
        referrer: request.referrer,
        isIframe: request.initialData?.isIframe ?? false,
      }

      const initialSetupResult = await performInitialSetup(setupRequest, dispatch)
      if (initialSetupResult.error !== null) {
        capture.warning('Checkout session without token')
        return initialSetupResult.error
      }
      const initialData = initialSetupResult.initialData as InitialData
      const browserCapabilities = {
        canSupportBiometric: initialSetupResult.browserCapabilities?.canSupportBiometric ?? false,
        isAuthenticatorAvailable: initialSetupResult.browserCapabilities?.isAuthenticatorAvailable ?? false,
      }

      // Step 2: Fetch & Validate Checkout
      const checkoutRequest: CheckoutFetchRequest = { country: request.country, skipPrefill: request.skipPrefill }
      const getCheckoutResponse = await fetchAndValidateCheckout(checkoutRequest, dispatch)

      if ('route' in getCheckoutResponse || 'crossRegionCheckoutUrl' in getCheckoutResponse) {
        // three possible route:
        // 1. terminal error
        // 2. skip prefill enter email
        // 3. cross region checkout url
        return getCheckoutResponse
      }

      const { checkout } = getCheckoutResponse
      const orderSource = checkout?.merchant?.source

      // The pre-login NUX only applies when a real consumer lookup actually ran and came back
      // net-new. performSequentialLookup only calls the backend when there is a valid prefilled
      // email; with no valid email (e.g. a mobile-only prefill or an empty token identity) it
      // short-circuits before any email/mobile lookup and hands back a seeded `{ existing: false }`
      // placeholder — that must NOT trigger the NUX. The consolidated lookup path always performs a
      // real lookup. Customers without a real lookup land on the email-entry screen and see the NUX
      // after entering their identity (handled in InitialLoading for the net-new sign-up path).
      const { email: prefilledEmail } = getValidUserEmailAndMobile({
        crossRegionUserSignInEmail: checkout?.crossRegionUserSignIn?.consumerEmail,
        checkoutEmail: checkout?.consumer?.email,
        checkoutMobile: checkout?.consumer?.phoneNumber,
        usernameFromCookie: request.usernameFromCookie,
      })
      const hasValidPrefilledEmail = !isUndefinedOrEmpty(prefilledEmail) && validateEmailFormat(prefilledEmail)
      const performedRealLookup = hasValidPrefilledEmail || checkout?.consumerLookup != null

      const maybeRedirectToAppleRetailEducationIntro = (result: InitialiseResult): InitialiseResult => {
        if (
          result == null ||
          !('route' in result) ||
          result.route === Route.TERMINAL_ERROR ||
          !isAppleRetailOrder(orderSource) ||
          !performedRealLookup ||
          !('lookup' in result) ||
          result.lookup?.existing !== false
        ) {
          return result
        }

        // Net-new customer with a prefilled identity (real lookup, existing === false): show the
        // NUX pre-login. Existing customers see it post-login via the AU regulation error handler.
        dispatch(setIntegrationContext({ nextRoute: result.route as Route }))
        return { ...result, route: Route.EDUCATION_INTRO }
      }

      // Step 3: Persistent Login
      const persistentLoginResult = await handlePersistentLogin(checkout, dispatch)

      if (persistentLoginResult !== null) {
        return maybeRedirectToAppleRetailEducationIntro(persistentLoginResult)
      }

      // Step 4: Consolidated Lookup Flow in getCheckoutData
      if (checkout.consumerLookup != null) {
        const consumerLookupRequest: ConsumerLookupRequest = {
          checkout,
          consumerLookup: checkout.consumerLookup,
          logEvent: request.logEvent,
          cashAuthRoute: request.cashAuthRoute,
        }
        const result = await handleConsolidatedConsumerLookup(consumerLookupRequest, dispatch)
        return maybeRedirectToAppleRetailEducationIntro(result)
      }

      // Step 5: Traditional Sequential Lookup
      // 5.1: Email lookup
      // 5.2: Mobile lookup
      const lookupDataRequest: LookupDataPreparationRequest = {
        checkout,
        country: request.country,
        validInitialData: initialData,
        browserCapabilities,
        usernameFromCookie: request.usernameFromCookie,
        biometricsEligibleAndRegisteredCheckTrigger: request.biometricsEligibleAndRegisteredCheckTrigger,
        otpLoginRouteAndTrigger: request.otpLoginRouteAndTrigger,
        rollout: request.rollout,
      }

      const lookupData = prepareLookupData(lookupDataRequest)

      const result = await performSequentialLookup(
        {
          ...lookupData,
          checkout,
        },
        dispatch
      )
      return maybeRedirectToAppleRetailEducationIntro(result)
    } catch (error) {
      return handleInitialiseError(error)
    }
  }
